/**
 * 设计令牌(规格 §3)—— **全仓唯一允许出现裸色值的文件**。
 *
 * 为什么要有这条规矩:颜色散在 15 个视图文件里之后,「主色」就不存在了 ——
 * 存在的只是 15 个差不多的蓝。改一处、漏一处,页面上会出现两个只差一点点的蓝,
 * 而**没有任何守卫看得见它**:`#2560E6` 与 `#2560E7` 都是合法的字符串,
 * 类型检查、构建、测试全部放行,只有人盯着看才发现。
 *
 * 令牌只在这里声明一次,视图里一律用 `var(--sd-*)`(见 tone-vars.ts)。
 * 组件内部需要的半透明色(标签底、告警底)也在这里 —— 半透明色同样是裸色值,
 * 写在组件里就是第二处真相来源。
 *
 * 值的来源是 docs/ui-design-spec.md §3,不从这里改:规格与代码不一致时,
 * 要么改规格要么改这里,不允许两边各说各话。
 */
/**
 * 主色块上的文字色(纯白)。
 *
 * 规格 §3 里这个白只以 `light.surface` 的形式出现过,但那是「面」的语义 ——
 * 拿它当文字色写,读的人会以为是写错了。两端的主按钮都要它,所以单独起个名字。
 * 小程序(§3.7)的 `onBrand` 是同一个值,所以它也只在这里写一次。
 */
const ON_BRAND = '#FFFFFF'

/**
 * §3.2 与 §3.7 **共用**的中性色:运营端(浅色)与小程序是同一组值。
 *
 * 为什么不各写一份:两份字面量各自都「看起来对」,而漂移之后没有任何东西看得见 ——
 * 一边改了、另一边没改,表现只是「小程序的白比后台的白差一点点」。
 * 同源的那一组值只在这个常量里出现一次,两个分组都从它展开。
 */
const LIGHT_NEUTRAL = {
  appBg: '#F4F6FA',
  surface: '#FFFFFF',
  surfaceAlt: '#FAFBFD',
  border: '#E6EAF2',
  borderStrong: '#D5DCE8',
  text1: '#0F1A2B',
  text2: '#4A5A72',
  text3: '#8494AB',
} as const

/**
 * §3.4 的四个语义色与那两档透明度。**提到 TOKENS 之前**是为了让小程序的状态标签配色
 * 能**引用**它们而不是把同一串值再写一遍:对象字面量内部引用不了 TOKENS 自己,
 * 而「蓝色底在小程序里又写一份 rgba(37,96,230,.12)」正是这个文件存在的理由要消灭的东西
 * (两个「差不多的蓝」在页面上分不出来,守卫也看不见)。
 *
 * 值一个字没动,只是从 TOKENS 里搬到这里 —— `TOKENS.semantic.*` 仍指同一批常量。
 */
const SEMANTIC_LIGHT = { success: '#0F9D58', warning: '#D98A00', danger: '#D93F3F', info: '#2560E6' } as const

const SEMANTIC_DARK = { success: '#2ED47A', warning: '#F5B141', danger: '#FF6B6B', info: '#4D8BF5' } as const

const SEMANTIC_ALPHA = {
  light: {
    brandBg: 'rgba(37,96,230,.10)',
    brandBorder: 'rgba(37,96,230,.22)',
    successBg: 'rgba(15,157,88,.12)',
    successBorder: 'rgba(15,157,88,.25)',
    warningBg: 'rgba(217,138,0,.12)',
    warningBorder: 'rgba(217,138,0,.25)',
    dangerBg: 'rgba(217,63,63,.12)',
    dangerBorder: 'rgba(217,63,63,.25)',
    infoBg: 'rgba(37,96,230,.12)',
    infoBorder: 'rgba(37,96,230,.25)',
  },
  dark: {
    brandBg: 'rgba(61,119,242,.18)',
    brandBorder: 'rgba(61,119,242,.34)',
    successBg: 'rgba(46,212,122,.12)',
    successBorder: 'rgba(46,212,122,.25)',
    warningBg: 'rgba(245,177,65,.12)',
    warningBorder: 'rgba(245,177,65,.25)',
    dangerBg: 'rgba(255,107,107,.12)',
    dangerBorder: 'rgba(255,107,107,.25)',
    infoBg: 'rgba(77,139,245,.12)',
    infoBorder: 'rgba(77,139,245,.25)',
  },
} as const

/**
 * §3.7 小程序**状态标签**的四组配色(G4,v2.1 —— 唯一的变动在 `active` 那一组)。
 *
 * 提成模块级常量(原来内联在 `TOKENS.miniapp.tag` 里)有两个原因:
 *   ① `active` 现在引用语义 info 那一组,而对象字面量内部引用不了 TOKENS 自己;
 *   ② 守卫要能把这张表单独取出来核对 —— 「状态→语义色」这条映射必须有人看着。
 *
 * **「进行中 / 运输中」= 蓝(G4)**:取值口径见下方各组的注释。蓝色底用的是
 * `SEMANTIC_ALPHA.light.infoBg`,不是设计稿里取样出来的 #E7F1FE —— 那个值与人眼
 * 分不出来的这一组差两个色阶,而「多一个差不多的蓝」正是令牌表要消灭的东西。
 */
const MINIAPP_TAG_COLORS = {
  /** 已取消 / 已退款:灰。设计稿取样的三个字面值,与设计稿逐字对上,本轮未动 */
  draft: { text: '#8494AB', bg: '#8494AB1F', border: '#8494AB40' },
  /**
   * 进行中 / 运输中:蓝(04 状态卡、07 订单卡、17「运行中」三处的取样都是这一个族)。
   *
   * **不再是品牌墨绿**:绿是小程序的品牌色 —— 主按钮、金额、品牌点缀都在用它,
   * 而「这一单正在跑」是一个**中性事实**,不是平台在推销什么。两者撞色的表现是
   * 「标签与按钮同色」,页面上完全正常,只有把两块并排看才发现层次没了。
   * 三个值全部来自语义 info 那一组(浅色档),一个字面值都不新增。
   */
  active: {
    text: SEMANTIC_LIGHT.info,
    bg: SEMANTIC_ALPHA.light.infoBg,
    border: SEMANTIC_ALPHA.light.infoBorder,
  },
  /** 待支付 / 待取货:橙(07 的「待支付」、17 的「待取货」)。设计稿取样值,本轮未动 */
  attention: { text: '#D98A00', bg: '#D98A001F', border: '#D98A0040' },
  /** 已完成 / 已送达:石板灰(07 的「已送达」)。设计稿取样值,本轮未动 */
  done: { text: '#57687C', bg: '#57687C1F', border: '#57687C40' },
} as const

export const TOKENS = {
  onBrand: ON_BRAND,

  /** §3.1 品牌色。浅色端主色取 600,深色端取 500 —— 深底上 600 偏闷 */
  brand: {
    50: '#EEF4FF',
    100: '#DCE7FE',
    200: '#C0D5FD',
    300: '#94B8FB',
    400: '#6697F8',
    500: '#3D77F2',
    600: '#2560E6',
    700: '#1C4CC7',
    800: '#1B3F9E',
    900: '#1B3778',
  },

  /** §3.2 运营端(浅色)。侧边栏是深色的 —— 浅色的内容区配深色导航是这一端的定位 */
  light: {
    ...LIGHT_NEUTRAL,
    sidebarBg: 'linear-gradient(180deg, #0C1728 0%, #101E33 100%)',
    /** 上面那条渐变的起点色,单独留一份:登录页左栏、深色横幅这类地方要的是纯色 */
    navDeep: '#0C1728',
    sidebarBorder: 'rgba(255,255,255,.06)',
    sidebarText: '#93A4BC',
    sidebarTextActive: '#FFFFFF',
    sidebarActiveBg: 'rgba(61,119,242,.16)',
    sidebarActiveBar: '#3D77F2',
  },

  /**
   * §3.3 监管端(深色)。bgDeep 给侧边栏与登录页品牌区,bgBase 给内容区。
   *
   * ═══ S-41 / AS-069(2026-10-16):运营端也翻深色,复用的就是这一组 ═══
   *
   * 甲方 2026-10-15 的精修稿把运营端整端画成了深色(决策 Y-31),画板侧新建了一组
   * 9 个 `ops-dark-*` 变量。2026-10-16 逐值核对:那 9 个值与本组**完全相等**
   * (#0A1120 / #060A12 / #0F1A2E / #132039 / rgba(120,160,220,.14) / rgba(120,160,220,.26) /
   * #E8EFFA / #93A7C4 / #5E7290),所以这里**不复制一组重复令牌** ——
   * 「ops-dark」落在 tone-vars.ts 的第三档 tone 上:内容区从本组取色,
   * 侧栏三件(渐变 / 文字 / 激活底)取 `light.sidebar*`,那三个值同样与画板逐值一致
   * (侧栏渐变 #0C1728→#101E33 = light.sidebarBg、激活底 #3D77F229 = light.sidebarActiveBg)。
   *
   * 将来两端深色若真的分叉(比如运营端要更亮一档的面板),那时才在这一层开第二组;
   * 现在开一组的全部收益,只是「两处字面量长得一样」—— 而代价是「改一处漏一处」。
   */
  dark: {
    bgDeep: '#060A12',
    bgBase: '#0A1120',
    panel: '#0F1A2E',
    panelAlt: '#132039',
    border: 'rgba(120,160,220,.14)',
    borderStrong: 'rgba(120,160,220,.26)',
    text1: '#E8EFFA',
    text2: '#93A7C4',
    text3: '#5E7290',
    accent: '#35D0F0',
    glow: 'rgba(61,119,242,.18)',
  },

  /** §3.4 语义色(值在文件顶部那三个常量里) */
  semantic: {
    light: SEMANTIC_LIGHT,
    dark: SEMANTIC_DARK,

    /**
     * 两档透明度:12% 底 + 25% 描边。
     *
     * 为什么不定成「组件里现算 opacity」:底色与描边要的是同一个色相的两个透明度,
     * 各写各的就会出现「告警块的红比标签的红深一点」这种没人报的偏差。
     * 两档写死在这里,想统一改的时候只有一个地方要动。
     */
    alpha: SEMANTIC_ALPHA,

    /**
     * 状态标签的四组配色(§4.3)。`unknown` 组就是 `draft` 组 ——
     * 未知状态显示成灰的、原样字符串,它**不是**一种新配色。
     */
    tag: {
      light: {
        draft: { text: '#8494AB', bg: 'rgba(132,148,171,.12)', border: 'rgba(132,148,171,.25)' },
        active: { text: '#2560E6', bg: 'rgba(37,96,230,.12)', border: 'rgba(37,96,230,.25)' },
        attention: { text: '#D98A00', bg: 'rgba(217,138,0,.12)', border: 'rgba(217,138,0,.25)' },
        done: { text: '#0F9D58', bg: 'rgba(15,157,88,.12)', border: 'rgba(15,157,88,.25)' },
      },
      dark: {
        draft: { text: '#93A7C4', bg: 'rgba(147,167,196,.12)', border: 'rgba(147,167,196,.25)' },
        active: { text: '#4D8BF5', bg: 'rgba(77,139,245,.12)', border: 'rgba(77,139,245,.25)' },
        attention: { text: '#F5B141', bg: 'rgba(245,177,65,.12)', border: 'rgba(245,177,65,.25)' },
        done: { text: '#2ED47A', bg: 'rgba(46,212,122,.12)', border: 'rgba(46,212,122,.25)' },
      },
    },
  },

  /**
   * §3.7 小程序品牌色阶(墨绿)—— Y-22(2026-10-11 人裁决)。
   *
   * 三端第一次出现两套品牌色。分野的判据是**用途不是端**:小程序是客户与承运商
   * 看到的对外产品,两个后台是对内工具,两者不共用一套视觉语言。
   * 这一组不是「第二处真相来源」—— 它就在这个文件里,与 `brand` 并排,
   * 「全仓唯一允许出现裸色值的文件」那句话因此继续成立。
   *
   * **它不许被两个后台端引用**:后台的主色是 `brand.*`(蓝)。混用的表现是
   * 「运营端某个按钮变成绿的」,而那在页面上完全正常,没有任何东西会报。
   */
  miniapp: {
    /** 品牌色阶。主色是 600(与两端同一个规矩:主色取 600) */
    brand: {
      50: '#F0F7F3',
      100: '#D9ECE2',
      200: '#B3D8C5',
      300: '#84BCA1',
      400: '#559B7C',
      500: '#34805F',
      600: '#1E6A4E',
      700: '#175542',
      800: '#134536',
      900: '#0F382C',
    },

    /** 中性色:与运营端同源(同一组值,从 LIGHT_NEUTRAL 展开,不另写一份) */
    neutral: { ...LIGHT_NEUTRAL, onBrand: ON_BRAND },

    /**
     * 语义色(浅色档,与 §3.4 同一组值)。**info 刻意保留蓝色** —— 它表示
     * 「平台给你的一条中性提示」,与品牌绿区分开:一条提示被染成品牌色之后,
     * 读的人会以为那是平台在推销什么。也正因为如此,状态标签的「进行中」那一组
     * 用的是它而不是品牌绿(G4)。
     */
    semantic: SEMANTIC_LIGHT,

    /**
     * 状态标签的四组配色。表在文件顶部(MINIAPP_TAG_COLORS),提上去的理由:
     * `active` 那一组引用语义 info,而对象字面量内部引用不了 TOKENS 自己;
     * 另外守卫要能把这张表单独取出来核对「状态→语义色」。
     */
    tag: MINIAPP_TAG_COLORS,

    /**
     * 客户首页(寄件)主卡的墨绿渐变与右上装饰圆(03 帧逐像素取样,2026-10-11)。
     *
     * 为什么整条渐变是一个字符串:它的**角度**也是测量的一部分(30deg,
     * 左下最深 → 右上最浅)。「视图里写角度、令牌里写两个端点色」会让改这条渐变
     * 要同时动两个文件,而角度错了的表现只是「绿的方向不太对」—— 没有任何东西会报。
     * 端点色:深端与 brand 900(#0F382C)逐通道差 ≤2,直接取它;浅端 #3A8B67
     * 不在任何色阶上,是从取样线性外推出来的(卡右上角被装饰圆盖住,量不到原色)。
     */
    heroBg: 'linear-gradient(30deg, #0F382C 0%, #3A8B67 100%)',

    /**
     * 主卡右上那个大圆的填充色。取样反推 ≈ **白色 8%**:
     * 圆盖住浅色区的实测色 #448A6C 与圆外沿(底色更暗处)的 #3B7960
     * 是同一条「底色 + 8% 白」的合成值 —— 设计稿里它不是一个实色圆,
     * 写实色会在底色深的那半边立刻偏色(而那看起来完全正常)。
     *
     * 13 块第二轮画板里的装饰圆(04 状态卡 170×170 白 7%、16 摘要卡 180×180 白 8%、
     * 18 摘要卡 176×176 白 7%)与它是同一种画法,差值 ≤1% 白 —— **复用这一条**,
     * 不另开 7%/6% 的档次:1% 的白色覆在墨绿上没有人的眼睛分得出来,
     * 而三个近乎同值的令牌正是「改一处漏一处」的起点。
     */
    heroGlow: 'rgba(255,255,255,.08)',

    /**
     * 绿卡上**有文字的白色小容器**(状态胶囊的底、复制小胶囊的底)——
     * v2.1 的 M1 / M15 两帧(S-34,2026-10-16)第一次用到它。
     *
     * 取样:M1 状态胶囊底 ≈ 白 15%(「白覆在墨绿上」的合成值 (69,127,105),
     * 底色 (39,109,82)),单号右端那枚复制小胶囊 ≈ 白 12% —— 同族、差 3%,
     * 取一个值(15%)。
     *
     * 与 heroGlow 分开的理由:heroGlow(8%)是**大面积装饰圆**的填充,它没有边、
     * 也不需要看清轮廓;这两个小胶囊是**有文字的容器**,再淡一档那枚字就浮在卡上、
     * 看不出「这是一枚胶囊」了。两者不是同一档,不合并。
     */
    onBrandFill: 'rgba(255,255,255,.15)',

    /**
     * 内页顶部的品牌卡渐变(画板 04 订单状态卡、18 作业摘要卡)。
     *
     * 与 heroBg 同族:同样的对角线渐变(左下深 → 右上浅),深端浅一档 ——
     * 画板 fill 是 brand-500 → brand-700(heroBg 那一档是 500 → 800)。
     *
     * 端点色与角度按 heroBg 同一条口径从画板量:PNG 逐像素取样、最小二乘拟出
     * 沿对角线的线性场,再把端点外推到卡片角点。所以端点色比画板 fill 里的
     * stop 色更深/更浅一档(#114C3C / #3A8965,而不是 #175542 / #34805F)——
     * 画板渲染时角点落在渐变线段之外(stop 只铺到 14.645%/85.355%),
     * 直接写 stop 色会让四个角都比画板淡一档。角度 17deg 是两处用量
     * (686×208 的模型角 16.9°、686×226 的 18.2°)的取整,差 1° 读不出来。
     */
    cardBg: 'linear-gradient(17deg, #114C3C 0%, #3A8965 100%)',

    /**
     * 已拍照片格的占位底(画板 09 订单详情下半的照片格、15 的营业执照格、
     * 18 的飞手缩略图、02 组件总览的 Photo)。
     *
     * 画板里这些格子的内容是深色渐变 + 白色图标的**占位素材**(真实照片的位置标记),
     * 页面照画板画的就是它。端点色同样是外推到角点的值(#121C19 / #697C73;
     * 画板 fill 的 stop 是 #1F2A26 / #5C6E66)。
     *
     * 角度 45° 取的是这几处用量的折中:09 照片格(140×192)模型角 53.9°、
     * 15 上传格(300×192)32.6°、18 缩略图(104×104)与组件总览方格 45°。
     * 它是深色低对比的底,±10° 在页面上读不出来 —— 不值得为它开三条同色令牌。
     */
    photoBg: 'linear-gradient(45deg, #121C19 0%, #697C73 100%)',

    /**
     * 占位照片格里那个图标的白(画板 fill #FFFFFF99 = 白 60%)。
     * 出现在三个页面、五个格子里,所以收一个名字:
     * 页面里写 rgba(255,255,255,.6) 或「白色 + opacity」都能画对,
     * 但**只有这一个值**;SdIcon 的 colorVar 也直接传 `--sd-photo-icon`。
     */
    photoIcon: '#FFFFFF99',

    /**
     * 分段控件(全部 / 进行中 / 已完成)的轨道底(画板 07 / 16 / 17 三页同款)。
     *
     * 画板取样是 #EDF1F7 —— 比 --sd-border(#E6EAF2)浅一档、偏蓝一点,
     * 白色选中胶囊浮在它上面才有「浮起来」的层次。它是三页共用的**控件底**,
     * 不是某一页的装饰:写回页面里就是三处裸色值(守卫 ②),
     * 而三处差一点点没有任何东西看得见。
     */
    segmentBg: '#EDF1F7',

    /**
     * 顶部面板式浮层顶上那条**深灰横条**(v2.1 的 C3 订阅通知面板;S-36 第一次用)。
     *
     * 画板取样 #575F6C —— 它恰好等于 text1(#0F1A2B)覆白 70%(逐通道差 ≤1),
     * 所以那条横条读起来与「面板的顶盖」是同一个灰。收成一个令牌而不是就地写
     * rgba(15,26,43,.7):「顶盖」这个语义后面还会有第二处(第二处的地方调一遍
     * 深浅,两个面板就会各长出一个几乎一样、又不一样的黑)。
     */
    panelCapBg: '#575F6C',

    /** 辅助块:主色与危险色的浅底 + 描边。两档透明度写死在这里,不许组件里现调 */
    aux: {
      brandBg: '#1E6A4E1A',
      brandBorder: '#1E6A4E38',
      dangerBg: '#D93F3F1F',
      dangerBorder: '#D93F3F40',
      /**
       * 弹层背后的遮罩(S-22 加)。取 text1(#0F1A2B)的 45%。
       * 它必须是一个令牌:弹层背景写 rgba(0,0,0,.4) 是视图里的裸色值(守卫 ②),
       * 而遮罩是那种「每个弹层随手调一个深浅」最典型的颜色 —— 三个弹层三层黑,
       * 页面上完全正常,只有把两个弹层并排看才发现不一样。
       */
      mask: 'rgba(15,26,43,.45)',
    },

    /**
     * 尺度(§3.5 与 §3.7 同源)。**单位是 rpx:数值 = 设计稿 px × 2**。
     *
     * 为什么 ×2:750rpx 就是屏宽,而在设计稿(375 宽)上 1px 换到小程序是 1rpx 的两倍。
     * 这份换算只做一次,写在这里 —— 页面里出现 `font-size: 40rpx` 这种裸值,
     * 就意味着「这一处别人不知道它是哪一档」,而它渲染出来完全正常(见守卫 ⑦)。
     *
     * `font` 这一组按 rpx 数值命名(s30 = 30rpx = 设计稿 15px),
     * 而不是按设计稿的 px 命名 —— 名字里写着 15、值是 30rpx 会更难读。
     * 设计稿那一列的对应关系:22↔11、26↔13、30↔15(正文)、34↔17、40↔20、48↔24、60↔30。
     */
    scale: {
      radius: { sm: '12rpx', md: '16rpx', lg: '24rpx', xl: '32rpx', pill: '999px' },
      space: {
        s4: '8rpx',
        s8: '16rpx',
        s12: '24rpx',
        s16: '32rpx',
        s20: '40rpx',
        s24: '48rpx',
        s32: '64rpx',
        s40: '80rpx',
      },
      font: {
        f11: '22rpx',
        f13: '26rpx',
        f15: '30rpx',
        f17: '34rpx',
        f20: '40rpx',
        f24: '48rpx',
        f30: '60rpx',
      },
      /** 正文行高。行高给倍数不给 rpx —— 它跟着字号走,不是一个固定尺寸 */
      lineHeight: { body: '1.6' },
    },
  },

  /** §3.5 尺度 */
  radius: { sm: '6px', md: '8px', lg: '12px', xl: '16px', pill: '999px' },

  space: {
    s4: '4px',
    s8: '8px',
    s12: '12px',
    s16: '16px',
    s20: '20px',
    s24: '24px',
    s32: '32px',
    s40: '40px',
  },

  shadow: {
    light: {
      xs: '0 1px 2px rgba(16,24,40,.05)',
      sm: '0 1px 3px rgba(16,24,40,.08), 0 1px 2px rgba(16,24,40,.04)',
      md: '0 4px 12px rgba(16,24,40,.08), 0 2px 4px rgba(16,24,40,.04)',
      lg: '0 12px 32px rgba(16,24,40,.10)',
    },
    dark: {
      sm: '0 0 0 1px rgba(120,160,220,.10), 0 2px 8px rgba(0,0,0,.35)',
      glow: '0 0 24px rgba(61,119,242,.16)',
    },
  },

  /** §3.6 字体。数字一律 tabular-nums —— 那是「看起来专业」最便宜的一招 */
  font: {
    sans: '-apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  },
} as const

/**
 * 三端的明暗档。组件的 tone 参数、令牌变量的取色分支都用它。
 *
 * `ops-dark` 是 2026-10-16 的 S-41 加进来的第三档:运营端按甲方精修稿整端翻深色
 * (AS-069 → 决策 Y-31)。它**不是第三组颜色** —— 内容区取 `dark` 家族(9 个值逐值
 * 相等,见 TOKENS.dark 上那段注释)、侧栏取 `light.sidebar*`,差异全在 tone-vars.ts
 * 的取色分支里。加这一档的理由:两端从此都是深色,但**侧栏与强调色的取法不同**
 * (监管端侧栏是 bgDeep 的平色、运营端是画板那条渐变),用一个 tone 值把差异
 * 收在一处,而不是让视图各自猜自己该配哪一组变量。
 */
export type Tone = 'light' | 'dark' | 'ops-dark'

/**
 * 「这个 tone 是不是深色系」的统一判据。
 *
 * 组件里 `tone === 'dark' ? … : …` 的写法在第三档出现之后全都错了 ——
 * `ops-dark` 会落进浅色那一支(深底上摆一块浅色控件,页面上只是「这一块不太对」)。
 * 判断深色一律走这里,新增深色档时不必满仓找三元表达式。
 */
export function isDarkTone(tone: Tone): boolean {
  return tone !== 'light'
}
